import MainButton from '@/components/ui/custom/buttons/MainButton';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import useToggle from '@/data/hook/useToggle';
import { deleteCoursePath } from '@/data/services/coursePathServices';
import { deleteCourse } from '@/data/services/courseServices';
import {
  courseBasesQueryKeys,
  coursePathsQueryKeys,
} from '@/data/services/querykeys';
import CoursePath from '@/models/CoursePath';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ROUTES } from '@/utils/routes';
import alert from '@/utils/shared/alert';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';

type DeleteActionProps = {
  coursePath: CoursePath;
};

export default function DeleteAction({ coursePath }: DeleteActionProps) {
  const { isOpen, toggle } = useToggle();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseBox.actions',
  });

  const onSuccess = () => {
    queryClient.removeQueries({
      queryKey: coursePathsQueryKeys.list._def,
    });
    queryClient.removeQueries({
      queryKey: courseBasesQueryKeys.list._def,
    });
    navigate(ROUTES.COURSES.BASE);
  };

  const { mutate: removeCoursePath, isPending: isRemovingCoursePath } =
    useMutation(deleteCoursePath, {
      onSuccess,
      onError(error) {
        alert.error(getErrorMessage(error));
      },
      onSettled: toggle,
    });

  const { mutate: removeCourseBase, isPending: isRemovingCourseBase } =
    useMutation(deleteCourse, {
      onSuccess,
      onError(error) {
        alert.error(getErrorMessage(error));
      },
      onSettled: toggle,
    });

  function handleAction() {
    if (coursePath.version > 1) {
      removeCoursePath(coursePath.slug);
    } else {
      removeCourseBase(coursePath.course.id);
    }
  }

  const loading = isRemovingCoursePath || isRemovingCourseBase;

  return (
    <Fragment>
      <ModalDisable
        onClickCancel={toggle}
        onClickConfirm={handleAction}
        translationString="modalDeleteCourse"
        visible={isOpen}
        modalType="delete"
        isRequesting={loading}
        selectedObjectName={coursePath.course.name}
      />
      <MainButton
        text={t('discard')}
        color="custom"
        onClick={toggle}
        className="font-500 !text-14 text-error gap-1 flex-row-reverse"
        icon={
          <TrashIcon
            className={'w-4 h-4 ease-in-out transition-all duration-200'}
            strokeWidth={3}
          />
        }
      />
    </Fragment>
  );
}
